// src/pages/CrewFormationForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { collection, getDocs, addDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { pickL } from '../utils/i18nContent';
import { trackGoogleAdsConversion } from '../utils/googleAds';

// Grille tarifaire Formation Crew (identique à CrewFormation.js — à garder synchronisée)
// Semestre 1 = sept-déc, Semestre 2 = jan-juin (montants différents, pas un flat 50/50)
const CREW_PRICING = {
  regulier: { annual: 600, semester1: 300, semester2: 450, monthly: 80, adminFee: 25 },
  avance: { annual: 1000, semester1: 450, semester2: 670, monthly: 120, adminFee: 25 }
};

const CrewFormationForm = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('crew');
  const [registrationType, setRegistrationType] = useState('child');
  const [students, setStudents] = useState([
    { firstName: '', lastName: '', birthDate: '', level: '', email: '', phone: '' },
  ]);

  const [parentData, setParentData] = useState({ 
    firstName: '', 
    lastName: '', 
    email: '', 
    phone: '' 
  });

  const [selectedFormation, setSelectedFormation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formations, setFormations] = useState([]);
  const [loadingFormations, setLoadingFormations] = useState(true);
  const [insuranceIncluded, setInsuranceIncluded] = useState(false);

  // Formule choisie : 'regulier' | 'avance' (plus de cours d'essai, sélection sur audition)
  const [formula, setFormula] = useState('regulier');
  const [paymentFrequency, setPaymentFrequency] = useState('annual');
  const [paymentError, setPaymentError] = useState(null);

  const handleStudentChange = (index, e) => {
    const { name, value } = e.target;
    const newStudents = [...students];
    newStudents[index] = { ...newStudents[index], [name]: value };
    setStudents(newStudents);
  };

  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setParentData({ ...parentData, [name]: value });
  };

  const handleTypeChange = (e) => {
    setRegistrationType(e.target.value);
    setStudents([{ firstName: '', lastName: '', birthDate: '', level: '', email: '', phone: '' }]);
  };

  const handleInsuranceChange = (e) => {
    setInsuranceIncluded(e.target.checked);
  };

  const addAnotherStudent = () => {
    setStudents([...students, { firstName: '', lastName: '', birthDate: '', level: '', email: '', phone: '' }]);
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return 0;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // Calcule les montants selon la formule + fréquence choisies.
  // Pour un abonnement récurrent (semestre/mensuel), le premier paiement inclut
  // les frais administratifs (25€) + l'assurance ; les échéances suivantes ne
  // comportent que le montant de base de la formule.
  const getAmounts = () => {
    const insuranceAmount = insuranceIncluded ? 25 * students.length : 0;
    const grid = CREW_PRICING[formula];

    if (paymentFrequency === 'semester') {
      // 1er paiement = semestre 1 (sept-déc) + frais admin ; l'unique échéance
      // récurrente restante = semestre 2 (jan-juin), montant différent du 1er.
      return {
        isRecurring: true,
        baseAmount: grid.semester1,
        adminFee: grid.adminFee,
        insuranceAmount,
        firstPayment: grid.semester1 + grid.adminFee + insuranceAmount,
        installmentAmount: grid.semester2,
        installmentsTotal: 2
      };
    }

    if (paymentFrequency === 'monthly') {
      return {
        isRecurring: true,
        baseAmount: grid.monthly,
        adminFee: grid.adminFee,
        insuranceAmount,
        firstPayment: grid.monthly + grid.adminFee + insuranceAmount,
        installmentAmount: grid.monthly,
        installmentsTotal: 10
      };
    }

    // annual : paiement unique
    return {
      isRecurring: false,
      baseAmount: grid.annual,
      adminFee: grid.adminFee,
      insuranceAmount,
      firstPayment: grid.annual + grid.adminFee + insuranceAmount,
      installmentAmount: null,
      installmentsTotal: null
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allStudentsValid = students.every(s => s.firstName && s.lastName && s.birthDate && s.level);
    if (!allStudentsValid) return alert(t('register.errors.fillStudents'));
    if (registrationType === 'child' && (!parentData.firstName || !parentData.lastName || !parentData.email)) {
      return alert(t('register.errors.fillParent'));
    }
    if (!selectedFormation) return alert(t('register.errors.selectFormation'));

    setIsSubmitting(true);
    try {
      let parentRef = null;
      let parentDocRef = null;
      const formation = formations.find(f => f.id === selectedFormation);
      const formationTitle = pickL(formation?.title_i18n ?? formation?.title, i18n.language, 'fr') || 'Formation Crew';

      if (registrationType === 'child') {
        const parentInfo = { 
          ...parentData, 
          role: 'Parent',
          registrationSource: 'site',
          createdAt: Timestamp.now() 
        };
        const parentUserRef = await addDoc(collection(db, 'users'), parentInfo);
        parentDocRef = await addDoc(collection(db, 'parents'), {
          parentId: parentUserRef.id,
          ...parentInfo,
          children: students.map(s => ({ id: null, firstName: s.firstName, lastName: s.lastName })),
          createdAt: serverTimestamp()
        });
        parentRef = parentUserRef;
      }

      const studentIds = [];
      for (const s of students) {
        const isMinor = calculateAge(s.birthDate) < 18;
        const role = isMinor ? 'Élève' : 'Élève adulte';
        const studentData = { 
          ...s, 
          role, 
          formation: selectedFormation,
          registrationSource: 'site',
          createdAt: Timestamp.now(), 
          parentId: isMinor && parentRef ? parentRef.id : null 
        };
        const studentRef = await addDoc(collection(db, 'users'), studentData);
        studentIds.push(studentRef.id);
        await addDoc(collection(db, 'students'), {
          userId: studentRef.id,
          firstName: s.firstName,
          lastName: s.lastName,
          birthDate: s.birthDate,
          level: s.level,
          email: s.email,
          phone: s.phone,
          parentId: isMinor && parentRef ? parentRef.id : null,
          formation: selectedFormation,
          createdAt: serverTimestamp()
        });
      }

      if (registrationType === 'child' && parentDocRef) {
        await updateDoc(parentDocRef, {
          children: students.map((s, i) => ({
            id: studentIds[i], 
            firstName: s.firstName, 
            lastName: s.lastName
          }))
        });
      }

      const amounts = getAmounts();
      const planId = `formation_crew_${formula}`; // formation_crew_regulier | _avance
      const contactEmail = registrationType === 'child' ? parentData.email : students[0]?.email;
      const contactName = registrationType === 'child'
        ? `${parentData.firstName} ${parentData.lastName}`
        : `${students[0]?.firstName || ''} ${students[0]?.lastName || ''}`;

      const registrationRef = await addDoc(collection(db, 'registrations'), {
        plan: planId,
        planName: `Formation Crew - ${formula === 'regulier' ? 'Régulière' : 'Avancée'}`,
        planDetails: {
          id: planId,
          name: formula === 'regulier' ? 'Formule régulière' : 'Formule avancée',
          type: 'formation_crew',
          price: amounts.baseAmount
        },
        formationLevel: formula,
        paymentFrequency: paymentFrequency,
        courseId: selectedFormation,
        courseTitle: formationTitle,
        registrationSource: 'site',
        source: 'site',
        persons: students.map((s, i) => ({ 
          id: studentIds[i], 
          firstName: s.firstName, 
          lastName: s.lastName, 
          birthDate: s.birthDate, 
          level: s.level 
        })),
        contactInfo: { 
          email: contactEmail, 
          phone: registrationType === 'child' ? parentData.phone : students[0]?.phone 
        },
        parentInfo: registrationType === 'child' ? { 
          parentId: parentRef.id, 
          parentFirstName: parentData.firstName, 
          parentLastName: parentData.lastName 
        } : null,
        paymentDetails: { 
          baseAmount: amounts.baseAmount, 
          adminFee: amounts.adminFee,
          insuranceAmount: amounts.insuranceAmount, 
          totalAmount: amounts.firstPayment, 
          paymentMethod: 'mollie'
        },
        insuranceIncluded,
        registrationType,
        locale: i18n.language,
        createdAt: serverTimestamp(),
        status: 'pending_payment'
      });

      trackGoogleAdsConversion();

      // --- Paiement Mollie ---
      // createPayment exige un utilisateur authentifié (même anonymement).
      const auth = getAuth();
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const createPayment = httpsCallable(functionsInstance, 'createPayment');

      const paymentPayload = {
        type: 'registration',
        refId: registrationRef.id,
        amount: amounts.firstPayment,
        description: `MKD Studio - Formation Crew (${formula})`
      };

      if (amounts.isRecurring) {
        paymentPayload.frequency = paymentFrequency; // 'monthly' | 'semester'
        paymentPayload.installmentAmount = amounts.installmentAmount;
        paymentPayload.installmentsTotal = amounts.installmentsTotal;
        paymentPayload.customerEmail = contactEmail;
        paymentPayload.customerName = contactName;
      }

      const result = await createPayment(paymentPayload);
      const checkoutUrl = result?.data?.checkoutUrl;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return; // On quitte la page : redirection vers Mollie
      }

      // Si jamais aucune URL n'est retournée, on affiche quand même la confirmation
      // (l'équipe pourra relancer le paiement manuellement depuis l'admin panel).
      setSubmitSuccess(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      console.error("Erreur lors de l'inscription:", error);
      setPaymentError(t('register.errors.payment', 'Une erreur est survenue lors du paiement. Merci de réessayer ou de nous contacter.'));
      alert(t('register.errors.submit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchFormations = async () => {
      try {
        const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
        const allCourses = coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const formationCourses = allCourses.filter(course => {
          const frTitle = (course?.title_i18n?.fr || course?.title || '').toLowerCase();
          return frTitle.includes('formation');
        });
        setFormations(formationCourses);
      } catch (error) {
        console.error("Erreur de chargement des formations:", error);
      } finally {
        setLoadingFormations(false);
      }
    };
    fetchFormations();
  }, []);

  const amounts = getAmounts();

  return (
    <div className="registration-container" style={{
      maxWidth: '900px',
      margin: '20px auto',
      padding: '20px',
      background: '#f9fafb',
      borderRadius: '16px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <h1 className="registration-title" style={{
        textAlign: 'center',
        color: '#7C3AED',
        marginBottom: '30px',
        fontSize: 'clamp(1.5rem, 5vw, 2.2rem)',
        fontWeight: '700'
      }}>
        {t('register.title')}
      </h1>

      <form onSubmit={handleSubmit} className="registration-form" style={{ display: 'grid', gap: '30px' }}>
        <div className="form-section" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
        }}>
          <h2 className="section-subtitle" style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
            fontWeight: '600',
            position: 'relative',
            paddingBottom: '10px'
          }}>
            {t('register.type.title')}
          </h2>
          <div className="radio-group" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <label className="radio-label" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              fontSize: '1rem',
              padding: '12px 15px',
              borderRadius: '10px',
              background: '#f9f9ff',
              border: '1px solid #e0e0f0',
              transition: 'all 0.2s'
            }}>
              <input type="radio" name="registrationType" value="child" checked={registrationType === 'child'} onChange={handleTypeChange} />
              <span>{t('register.type.child')}</span>
            </label>
            <label className="radio-label" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              fontSize: '1rem',
              padding: '12px 15px',
              borderRadius: '10px',
              background: '#f9f9ff',
              border: '1px solid #e0e0f0',
              transition: 'all 0.2s'
            }}>
              <input type="radio" name="registrationType" value="adult" checked={registrationType === 'adult'} onChange={handleTypeChange} />
              <span>{t('register.type.adult')}</span>
            </label>
          </div>
        </div>

        <div className="form-section" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
        }}>
          <h2 className="section-subtitle" style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
            fontWeight: '600',
            position: 'relative',
            paddingBottom: '10px'
          }}>
            {t('register.formation.title')}
          </h2>
          <div className="form-group">
            <select value={selectedFormation} onChange={(e) => setSelectedFormation(e.target.value)} required className="form-input" disabled={loadingFormations} style={{
              padding: '14px',
              border: '1px solid #e0e0f0',
              borderRadius: '10px',
              fontSize: '16px',
              transition: 'all 0.3s',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <option value="">{loadingFormations ? t('register.formation.loading') : t('register.formation.placeholder')}</option>
              {formations.map(formation => (
                <option key={formation.id} value={formation.id}>
                  {pickL(formation.title_i18n ?? formation.title, i18n.language, 'fr')}
                </option>
              ))}
              {!loadingFormations && formations.length === 0 && <option disabled>{t('register.formation.empty')}</option>}
            </select>
          </div>
        </div>

        <div className="form-section" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
        }}>
          <h2 className="section-subtitle" style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
            fontWeight: '600',
            position: 'relative',
            paddingBottom: '10px'
          }}>
            Formule
          </h2>
          <div className="formula-group" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { id: 'regulier', label: 'Formule régulière', desc: `${CREW_PRICING.regulier.annual}€/an, ${CREW_PRICING.regulier.semester1}€ + ${CREW_PRICING.regulier.semester2}€/semestre ou ${CREW_PRICING.regulier.monthly}€/mois` },
              { id: 'avance', label: 'Formule avancée', desc: `${CREW_PRICING.avance.annual}€/an, ${CREW_PRICING.avance.semester1}€ + ${CREW_PRICING.avance.semester2}€/semestre ou ${CREW_PRICING.avance.monthly}€/mois` }
            ].map(opt => (
              <label key={opt.id} className="radio-label" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '4px',
                cursor: 'pointer',
                padding: '12px 15px',
                borderRadius: '10px',
                background: formula === opt.id ? '#efedff' : '#f9f9ff',
                border: formula === opt.id ? '2px solid #7C3AED' : '1px solid #e0e0f0',
                transition: 'all 0.2s'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
                  <input type="radio" name="formula" value={opt.id} checked={formula === opt.id} onChange={(e) => setFormula(e.target.value)} />
                  {opt.label}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '24px' }}>{opt.desc}</span>
              </label>
            ))}
          </div>

          <div style={{ marginTop: '20px' }}>
              <h3 style={{ color: '#7C3AED', fontSize: '1rem', marginBottom: '10px' }}>Fréquence de paiement</h3>
              <div className="formula-group" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { id: 'annual', label: 'Annuel', desc: `${CREW_PRICING[formula].annual}€ en 1 seule fois + ${CREW_PRICING[formula].adminFee}€ de frais admin` },
                  { id: 'semester', label: 'Semestriel', desc: `Sept-déc: ${CREW_PRICING[formula].semester1}€ + ${CREW_PRICING[formula].adminFee}€ frais admin, puis jan-juin: ${CREW_PRICING[formula].semester2}€` },
                  { id: 'monthly', label: 'Mensuel (domiciliation)', desc: `10 x ${CREW_PRICING[formula].monthly}€, 1er paiement + ${CREW_PRICING[formula].adminFee}€ de frais admin` }
                ].map(opt => (
                  <label key={opt.id} className="radio-label" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '4px',
                    cursor: 'pointer',
                    padding: '12px 15px',
                    borderRadius: '10px',
                    background: paymentFrequency === opt.id ? '#efedff' : '#f9f9ff',
                    border: paymentFrequency === opt.id ? '2px solid #7C3AED' : '1px solid #e0e0f0',
                    transition: 'all 0.2s'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
                      <input type="radio" name="paymentFrequency" value={opt.id} checked={paymentFrequency === opt.id} onChange={(e) => setPaymentFrequency(e.target.value)} />
                      {opt.label}
                    </span>
                    <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '24px' }}>{opt.desc}</span>
                  </label>
                ))}
              </div>
              {(paymentFrequency === 'monthly' || paymentFrequency === 'semester') && (
                <p className="info-text" style={{ marginTop: '10px' }}>
                  💡 Le premier paiement crée un mandat de domiciliation SEPA (via Mollie). Les paiements suivants seront prélevés automatiquement aux échéances.
                </p>
              )}
            </div>
        </div>

        <div className="form-section" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
        }}>
          <h2 className="section-subtitle" style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
            fontWeight: '600',
            position: 'relative',
            paddingBottom: '10px'
          }}>
            {registrationType === 'child' ? t('register.students.childrenTitle') : t('register.students.adultsTitle')}
          </h2>

          {students.map((student, index) => (
            <div key={index} className="person-form" style={{
              padding: '20px',
              borderRadius: '12px',
              background: '#f9f9ff',
              border: '1px solid #e0e0f0',
              marginBottom: '15px'
            }}>
              <div className="form-group-header" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <h3 style={{ color: '#7C3AED', fontSize: '1.1rem', margin: 0 }}>
                  {registrationType === 'child' ? `${t('register.students.child')} ${index + 1}` : `${t('register.students.adult')} ${index + 1}`}
                </h3>
                {students.length > 1 && (
                  <button type="button" className="remove-button" onClick={() => {
                    const newStudents = [...students]; newStudents.splice(index, 1); setStudents(newStudents);
                  }} style={{
                    padding: '6px 12px',
                    background: '#fff0f0',
                    color: '#e74c3c',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    transition: 'background 0.2s'
                  }}>
                    {t('register.students.remove')}
                  </button>
                )}
              </div>

              <div className="form-grid" style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '15px'
              }}>
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.firstName')} *</label>
                  <input type="text" name="firstName" value={student.firstName} onChange={(e) => handleStudentChange(index, e)} required className="form-input" style={{
                    padding: '14px',
                    border: '1px solid #e0e0f0',
                    borderRadius: '10px',
                    fontSize: '16px',
                    transition: 'all 0.3s'
                  }} />
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.lastName')} *</label>
                  <input type="text" name="lastName" value={student.lastName} onChange={(e) => handleStudentChange(index, e)} required className="form-input" style={{
                    padding: '14px',
                    border: '1px solid #e0e0f0',
                    borderRadius: '10px',
                    fontSize: '16px',
                    transition: 'all 0.3s'
                  }} />
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.birthDate')} *</label>
                  <input type="date" name="birthDate" value={student.birthDate} onChange={(e) => handleStudentChange(index, e)} required className="form-input" style={{
                    padding: '14px',
                    border: '1px solid #e0e0f0',
                    borderRadius: '10px',
                    fontSize: '16px',
                    transition: 'all 0.3s'
                  }} />
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.level')} *</label>
                  <select name="level" value={student.level} onChange={(e) => handleStudentChange(index, e)} required className="form-input" style={{
                    padding: '14px',
                    border: '1px solid #e0e0f0',
                    borderRadius: '10px',
                    fontSize: '16px',
                    transition: 'all 0.3s'
                  }}>
                    <option value="">{t('register.fields.levelPlaceholder')}</option>
                    <option value="Débutant">{t('register.levels.beginner')}</option>
                    <option value="Intermédiaire">{t('register.levels.intermediate')}</option>
                    <option value="Avancé">{t('register.levels.advanced')}</option>
                  </select>
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.email')}</label>
                  <input type="email" name="email" value={student.email} onChange={(e) => handleStudentChange(index, e)} className="form-input" style={{
                    padding: '14px',
                    border: '1px solid #e0e0f0',
                    borderRadius: '10px',
                    fontSize: '16px',
                    transition: 'all 0.3s'
                  }} />
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.phone')}</label>
                  <input type="tel" name="phone" value={student.phone} onChange={(e) => handleStudentChange(index, e)} className="form-input" style={{
                    padding: '14px',
                    border: '1px solid #e0e0f0',
                    borderRadius: '10px',
                    fontSize: '16px',
                    transition: 'all 0.3s'
                  }} />
                </div>
              </div>
            </div>
          ))}

          <button type="button" className="add-button" onClick={addAnotherStudent} style={{
            padding: '12px 15px',
            background: 'none',
            color: '#7C3AED',
            border: '1px dashed #7C3AED',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'center',
            width: '100%',
            fontSize: '1rem'
          }}>
            + {t('register.students.addAnother', { type: registrationType === 'child' ? t('register.students.child').toLowerCase() : t('register.students.adult').toLowerCase() })}
          </button>
        </div>

        {registrationType === 'child' && (
          <div className="form-section" style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
          }}>
            <h2 className="section-subtitle" style={{
              color: '#7C3AED',
              marginBottom: '15px',
              fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
              fontWeight: '600',
              position: 'relative',
              paddingBottom: '10px'
            }}>
              {t('register.parent.title')}
            </h2>
            <div className="form-grid" style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '15px'
            }}>
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.firstName')} *</label>
                <input type="text" name="firstName" value={parentData.firstName} onChange={handleParentChange} required className="form-input" style={{
                  padding: '14px',
                  border: '1px solid #e0e0f0',
                  borderRadius: '10px',
                  fontSize: '16px',
                  transition: 'all 0.3s'
                }} />
              </div>
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.lastName')} *</label>
                <input type="text" name="lastName" value={parentData.lastName} onChange={handleParentChange} required className="form-input" style={{
                  padding: '14px',
                  border: '1px solid #e0e0f0',
                  borderRadius: '10px',
                  fontSize: '16px',
                  transition: 'all 0.3s'
                }} />
              </div>
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.email')} *</label>
                <input type="email" name="email" value={parentData.email} onChange={handleParentChange} required className="form-input" style={{
                  padding: '14px',
                  border: '1px solid #e0e0f0',
                  borderRadius: '10px',
                  fontSize: '16px',
                  transition: 'all 0.3s'
                }} />
              </div>
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ marginBottom: '8px', fontWeight: '500', color: '#444', fontSize: '0.95rem' }}>{t('register.fields.phone')}</label>
                <input type="tel" name="phone" value={parentData.phone} onChange={handleParentChange} className="form-input" style={{
                  padding: '14px',
                  border: '1px solid #e0e0f0',
                  borderRadius: '10px',
                  fontSize: '16px',
                  transition: 'all 0.3s'
                }} />
              </div>
            </div>
          </div>
        )}

        <div className="form-section" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
        }}>
          <h2 className="section-subtitle" style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
            fontWeight: '600',
            position: 'relative',
            paddingBottom: '10px'
          }}>
            {t('register.insurance.title')}
          </h2>
          <div style={{
            backgroundColor: '#f0f9ff',
            padding: '15px',
            borderRadius: '10px',
            border: '1px solid #e0e0f0'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              marginBottom: '10px'
            }}>
              <input
                type="checkbox"
                checked={insuranceIncluded}
                onChange={handleInsuranceChange}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '500' }}>{t('register.insurance.label')} (25€ par personne)</span>
            </label>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
              {t('register.insurance.description')}
            </p>
          </div>
        </div>

        <div className="form-section" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
        }}>
          <h2 className="section-subtitle" style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
            fontWeight: '600',
            position: 'relative',
            paddingBottom: '10px'
          }}>
            {t('register.summary.title')}
          </h2>
          <div style={{
            backgroundColor: '#f9f9ff',
            padding: '20px',
            borderRadius: '10px',
            border: '1px solid #e0e0f0'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '10px',
              paddingBottom: '10px',
              borderBottom: '1px solid #e0e0f0'
            }}>
              <span style={{ fontWeight: '500' }}>{t('register.summary.course')}:</span>
              <span style={{ fontWeight: '600', color: '#7C3AED' }}>
                {`Formation Crew — Formule ${formula === 'regulier' ? 'régulière' : 'avancée'}`}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '10px',
              paddingBottom: '10px',
              borderBottom: '1px solid #e0e0f0'
            }}>
              <span style={{ fontWeight: '500' }}>{t('register.summary.persons')}:</span>
              <span>{students.length}</span>
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '10px',
                paddingBottom: '10px',
                borderBottom: '1px solid #e0e0f0'
              }}>
                <span style={{ fontWeight: '500' }}>Fréquence:</span>
                <span>{paymentFrequency === 'annual' ? 'Annuel' : paymentFrequency === 'semester' ? 'Semestriel' : 'Mensuel'}</span>
              </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '10px',
              paddingBottom: '10px',
              borderBottom: '1px solid #e0e0f0'
            }}>
              <span style={{ fontWeight: '500' }}>Montant de la formule:</span>
              <span>{amounts.baseAmount}€</span>
            </div>
            {amounts.adminFee > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '10px',
                paddingBottom: '10px',
                borderBottom: '1px solid #e0e0f0'
              }}>
                <span style={{ fontWeight: '500' }}>Frais administratifs:</span>
                <span>{amounts.adminFee}€</span>
              </div>
            )}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '10px',
              paddingBottom: '10px',
              borderBottom: '1px solid #e0e0f0'
            }}>
              <span style={{ fontWeight: '500' }}>{t('register.summary.insurance')}:</span>
              <span>{insuranceIncluded ? `${amounts.insuranceAmount}€` : '0€'}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '15px',
              fontSize: '1.2rem',
              fontWeight: '700',
              color: '#7C3AED'
            }}>
              <span>{amounts.isRecurring ? 'Premier paiement' : t('register.summary.total')}:</span>
              <span>{amounts.firstPayment}€</span>
            </div>
            {amounts.isRecurring && (
              <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                puis {amounts.installmentsTotal - 1} x {amounts.installmentAmount}€ ({paymentFrequency === 'monthly' ? 'chaque mois' : 'au semestre suivant'})
              </p>
            )}
          </div>
        </div>

        <div className="form-section" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
        }}>
          <h2 className="section-subtitle" style={{
            color: '#7C3AED',
            marginBottom: '15px',
            fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
            fontWeight: '600',
            position: 'relative',
            paddingBottom: '10px'
          }}>
            {t('register.payment.title')}
          </h2>
          <div style={{ marginBottom: '10px' }}>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              {amounts.isRecurring
                ? `En cliquant sur "S'inscrire", tu seras redirigé·e vers Mollie pour régler ${amounts.firstPayment}€ et mettre en place le mandat de domiciliation SEPA pour les paiements suivants.`
                : `En cliquant sur "S'inscrire", tu seras redirigé·e vers notre page de paiement sécurisée Mollie pour régler ${amounts.firstPayment}€.`}
            </p>
            {paymentError && (
              <p className="error-text">{paymentError}</p>
            )}
          </div>
        </div>

        <div className="form-actions" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
          <button type="submit" className="submit-button" disabled={isSubmitting} style={{
            padding: '14px 30px',
            background: '#7C3AED',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            transition: 'background 0.3s',
            width: '100%',
            maxWidth: '300px'
          }}>
            {isSubmitting ? t('register.submit.loading') : t('register.submit.cta')}
          </button>
        </div>

        {submitSuccess && <div className="success-message" style={{
          marginTop: '20px',
          padding: '15px',
          background: '#e8f5e9',
          color: '#2e7d32',
          borderRadius: '10px',
          textAlign: 'center',
          fontWeight: '500'
        }}>{t('register.submit.success')}</div>}
      </form>

      <style>{`
        @media (min-width: 480px) {
          .form-group-header {
            flex-direction: row !important;
          }
        }
        
        @media (min-width: 640px) {
          .form-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          
          .radio-group {
            flex-direction: row !important;
            gap: 15px !important;
          }
        }
        
        @media (min-width: 768px) {
          .registration-container {
            margin: 40px auto !important;
            padding: 30px !important;
          }
          
          .form-grid {
            gap: 20px !important;
          }
        }
        
        @media (min-width: 1024px) {
          .form-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CrewFormationForm;